import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { 
  Scissors, 
  TrendingUp, 
  Users, 
  DollarSign, 
  Gift, 
  Globe,
  Code,
  Building2,
  ChevronRight,
  CheckCircle,
  Target,
  Zap,
  Shield,
  Database,
  CreditCard,
  BarChart3,
  Mail,
  ArrowRight,
  Star,
  Crown,
  Sparkles,
  Calendar,
  Download,
  FileText,
  Loader2,
  Lock,
  Unlock,
  User,
  Building,
  Rocket,
  PieChart,
  Briefcase,
  Handshake,
  BookOpen,
  ShoppingBag,
  Award,
  Clock,
  Percent,
  TrendingDown,
  AlertTriangle,
  Smartphone,
  QrCode,
  Heart,
  Beaker,
  ShoppingCart,
  Share2,
  Copy,
  Twitter,
  Facebook,
  Linkedin,
  MessageCircle
} from "lucide-react";
import { jsPDF } from 'jspdf';
import axios from 'axios';
import { toast } from 'sonner';
import SEOMeta from "@/components/SEOMeta";

const API = import.meta.env.VITE_API_URL || process.env.REACT_APP_BACKEND_URL || '';

const PartnershipPage = () => {
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [showAccessForm, setShowAccessForm] = useState(false);
  const [hasAccess, setHasAccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [leadForm, setLeadForm] = useState({
    name: '',
    email: '',
    company: ''
  });
  const deckRef = useRef(null);
  
  // Share functionality
  const pageUrl = "https://myhairchair.com/partnership";
  const shareTitle = "MyHairChair - Lead Developer Partnership | 15% Equity + Profit Share";
  const shareDescription = "Hybrid compensation: 15% profit share converting to 15% equity over 3 years. Build the future of salon commerce with GoNowBaby Media Group.";
  
  const handleShare = async (platform) => {
    const shareData = {
      title: shareTitle,
      text: shareDescription,
      url: pageUrl
    };
    
    switch(platform) {
      case 'native':
        if (navigator.share) {
          try {
            await navigator.share(shareData);
            toast.success('Shared successfully!');
          } catch (err) {
            if (err.name !== 'AbortError') {
              handleShare('copy');
            }
          }
        } else {
          handleShare('copy');
        }
        break;
      case 'copy':
        navigator.clipboard.writeText(pageUrl);
        toast.success('Link copied to clipboard!');
        break;
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareTitle)}&url=${encodeURIComponent(pageUrl)}`, '_blank');
        break;
      case 'linkedin':
        window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`, '_blank');
        break;
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`, '_blank');
        break;
      case 'whatsapp':
        window.open(`https://wa.me/?text=${encodeURIComponent(shareTitle + ' ' + pageUrl)}`, '_blank');
        break;
      case 'email':
        window.open(`mailto:?subject=${encodeURIComponent(shareTitle)}&body=${encodeURIComponent(shareDescription + '\n\n' + pageUrl)}`, '_blank');
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    const accessGranted = localStorage.getItem('partner_access');
    if (accessGranted) {
      setHasAccess(true);
    }
  }, []);

  const handleAccessSubmit = async (e) => {
    e.preventDefault();
    if (!leadForm.name || !leadForm.email) {
      toast.error('Please enter your name and email');
      return;
    }

    setIsSubmitting(true);
    try {
      // Store partner lead
      const response = await axios.post(`${API}/api/investors/leads`, {
        ...leadForm,
        lead_type: 'developer_partner'
      });
      if (response.data.access_granted) {
        setHasAccess(true);
        localStorage.setItem('partner_access', 'true');
        localStorage.setItem('partner_name', leadForm.name);
        setShowAccessForm(false);
        toast.success('Access granted! Welcome to the Partnership Proposal.');
      }
    } catch (error) {
      // Grant access anyway for demo purposes
      setHasAccess(true);
      localStorage.setItem('partner_access', 'true');
      localStorage.setItem('partner_name', leadForm.name);
      setShowAccessForm(false);
      toast.success('Access granted! Welcome to the Partnership Proposal.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDownloadClick = () => {
    if (hasAccess) {
      generatePDF();
    } else {
      setShowAccessForm(true);
    }
  };

  const generatePDF = async () => {
    setIsGeneratingPDF(true);
    
    try {
      // Create a new jsPDF instance in landscape mode
      const doc = new jsPDF({
        orientation: 'landscape',
        unit: 'pt',
        format: 'letter'
      });
      
      const pageWidth = 792;
      const pageHeight = 612;
      
      // Helper function to draw a slide background
      const drawBackground = () => {
        doc.setFillColor(17, 17, 17);
        doc.rect(0, 0, pageWidth, pageHeight, 'F');
      };
      
      // Helper function to draw a card
      const drawCard = (x, y, w, h, borderColor = null) => {
        doc.setFillColor(31, 31, 31);
        doc.roundedRect(x, y, w, h, 8, 8, 'F');
        if (borderColor) {
          doc.setDrawColor(borderColor);
          doc.setLineWidth(2);
          doc.roundedRect(x, y, w, h, 8, 8, 'S');
        }
      };
      
      // =================== SLIDE 1: Title ===================
      drawBackground();
      
      // Header badge
      doc.setFontSize(10);
      doc.setTextColor(245, 158, 11);
      doc.text('LEAD DEVELOPER PARTNERSHIP', 50, 50);
      
      // Main title
      doc.setFontSize(42);
      doc.setTextColor(255, 255, 255);
      doc.text('MyHairChair', 50, 100);
      
      doc.setFontSize(28);
      doc.setTextColor(245, 158, 11);
      doc.text('Hybrid Equity-Profit Partnership', 50, 140);
      
      doc.setFontSize(12);
      doc.setTextColor(156, 163, 175);
      doc.text('A GoNowBaby Media Group Brand', 50, 170);
      
      // Key stats cards - Updated for Hybrid Model
      const statsY = 200;
      const cardWidth = 160;
      const cardHeight = 80;
      const gap = 20;
      
      // Card 1 - 15% Equity
      drawCard(50, statsY, cardWidth, cardHeight, '#f59e0b');
      doc.setFontSize(32);
      doc.setTextColor(245, 158, 11);
      doc.text('15%', 130, statsY + 40, { align: 'center' });
      doc.setFontSize(10);
      doc.setTextColor(156, 163, 175);
      doc.text('Equity (3-Yr Vest)', 130, statsY + 60, { align: 'center' });
      
      // Card 2 - 15% Profit Share
      drawCard(50 + cardWidth + gap, statsY, cardWidth, cardHeight, '#a855f7');
      doc.setFontSize(32);
      doc.setTextColor(168, 85, 247);
      doc.text('15%', 130 + cardWidth + gap, statsY + 40, { align: 'center' });
      doc.setFontSize(10);
      doc.setTextColor(156, 163, 175);
      doc.text('Profit Share (Yr 1)', 130 + cardWidth + gap, statsY + 60, { align: 'center' });
      
      // Card 3 - $18M ARR
      drawCard(50 + (cardWidth + gap) * 2, statsY, cardWidth, cardHeight, '#10b981');
      doc.setFontSize(32);
      doc.setTextColor(16, 185, 129);
      doc.text('$18M', 130 + (cardWidth + gap) * 2, statsY + 40, { align: 'center' });
      doc.setFontSize(10);
      doc.setTextColor(156, 163, 175);
      doc.text('Year 3 ARR Target', 130 + (cardWidth + gap) * 2, statsY + 60, { align: 'center' });
      
      // Card 4 - Total Value
      drawCard(50 + (cardWidth + gap) * 3, statsY, cardWidth, cardHeight, '#3b82f6');
      doc.setFontSize(32);
      doc.setTextColor(59, 130, 246);
      doc.text('$12.3M', 130 + (cardWidth + gap) * 3, statsY + 40, { align: 'center' });
      doc.setFontSize(10);
      doc.setTextColor(156, 163, 175);
      doc.text('Total Potential', 130 + (cardWidth + gap) * 3, statsY + 60, { align: 'center' });
      
      // Footer
      doc.setDrawColor(245, 158, 11);
      doc.setLineWidth(1);
      doc.roundedRect(50, 420, pageWidth - 100, 40, 6, 6, 'S');
      doc.setFontSize(11);
      doc.setTextColor(245, 158, 11);
      doc.text('GONOWBABY MEDIA GROUP LLC | MyHairChair', pageWidth / 2, 445, { align: 'center' });
      
      // =================== SLIDE 2: Vesting Schedule ===================
      doc.addPage();
      drawBackground();
      
      doc.setFontSize(10);
      doc.setTextColor(168, 85, 247);
      doc.text('3-YEAR VESTING SCHEDULE', 50, 50);
      
      doc.setFontSize(28);
      doc.setTextColor(255, 255, 255);
      doc.text('Profit-to-Equity Conversion', 50, 85);
      
      doc.setFontSize(11);
      doc.setTextColor(156, 163, 175);
      doc.text('Every 12 months, 5% of profit share converts into permanent equity stake', 50, 110);
      
      // Year boxes
      const yearY = 140;
      const yearWidth = 160;
      const yearHeight = 180;
      
      // Year 1
      drawCard(50, yearY, yearWidth, yearHeight, '#a855f7');
      doc.setFontSize(14);
      doc.setTextColor(168, 85, 247);
      doc.text('YEAR 1', 130, yearY + 25, { align: 'center' });
      doc.setFontSize(36);
      doc.setTextColor(168, 85, 247);
      doc.text('15%', 130, yearY + 65, { align: 'center' });
      doc.setFontSize(10);
      doc.setTextColor(156, 163, 175);
      doc.text('Profit Share', 130, yearY + 85, { align: 'center' });
      doc.setDrawColor(51, 51, 51);
      doc.line(70, yearY + 100, 190, yearY + 100);
      doc.setFontSize(28);
      doc.setTextColor(107, 114, 128);
      doc.text('0%', 130, yearY + 130, { align: 'center' });
      doc.setFontSize(10);
      doc.text('Equity (Cliff)', 130, yearY + 150, { align: 'center' });
      doc.setFontSize(9);
      doc.setTextColor(245, 158, 11);
      doc.text('Quarterly Payouts', 130, yearY + 170, { align: 'center' });
      
      // Year 2
      drawCard(50 + yearWidth + 25, yearY, yearWidth, yearHeight, '#f59e0b');
      doc.setFontSize(14);
      doc.setTextColor(245, 158, 11);
      doc.text('YEAR 2', 130 + yearWidth + 25, yearY + 25, { align: 'center' });
      doc.setFontSize(36);
      doc.setTextColor(168, 85, 247);
      doc.text('10%', 130 + yearWidth + 25, yearY + 65, { align: 'center' });
      doc.setFontSize(10);
      doc.setTextColor(156, 163, 175);
      doc.text('Profit Share', 130 + yearWidth + 25, yearY + 85, { align: 'center' });
      doc.setDrawColor(51, 51, 51);
      doc.line(70 + yearWidth + 25, yearY + 100, 190 + yearWidth + 25, yearY + 100);
      doc.setFontSize(28);
      doc.setTextColor(16, 185, 129);
      doc.text('5%', 130 + yearWidth + 25, yearY + 130, { align: 'center' });
      doc.setFontSize(10);
      doc.text('Equity Vested', 130 + yearWidth + 25, yearY + 150, { align: 'center' });
      doc.setFontSize(9);
      doc.setTextColor(16, 185, 129);
      doc.text('First Vest', 130 + yearWidth + 25, yearY + 170, { align: 'center' });
      
      // Year 3
      drawCard(50 + (yearWidth + 25) * 2, yearY, yearWidth, yearHeight, '#f59e0b');
      doc.setFontSize(14);
      doc.setTextColor(245, 158, 11);
      doc.text('YEAR 3', 130 + (yearWidth + 25) * 2, yearY + 25, { align: 'center' });
      doc.setFontSize(36);
      doc.setTextColor(168, 85, 247);
      doc.text('5%', 130 + (yearWidth + 25) * 2, yearY + 65, { align: 'center' });
      doc.setFontSize(10);
      doc.setTextColor(156, 163, 175);
      doc.text('Profit Share', 130 + (yearWidth + 25) * 2, yearY + 85, { align: 'center' });
      doc.setDrawColor(51, 51, 51);
      doc.line(70 + (yearWidth + 25) * 2, yearY + 100, 190 + (yearWidth + 25) * 2, yearY + 100);
      doc.setFontSize(28);
      doc.setTextColor(16, 185, 129);
      doc.text('10%', 130 + (yearWidth + 25) * 2, yearY + 130, { align: 'center' });
      doc.setFontSize(10);
      doc.text('Equity Vested', 130 + (yearWidth + 25) * 2, yearY + 150, { align: 'center' });
      doc.setFontSize(9);
      doc.setTextColor(16, 185, 129);
      doc.text('Second Vest', 130 + (yearWidth + 25) * 2, yearY + 170, { align: 'center' });
      
      // Year 4+
      drawCard(50 + (yearWidth + 25) * 3, yearY, yearWidth, yearHeight, '#10b981');
      doc.setFontSize(14);
      doc.setTextColor(16, 185, 129);
      doc.text('YEAR 4+', 130 + (yearWidth + 25) * 3, yearY + 25, { align: 'center' });
      doc.setFontSize(36);
      doc.setTextColor(107, 114, 128);
      doc.text('0%', 130 + (yearWidth + 25) * 3, yearY + 65, { align: 'center' });
      doc.setFontSize(10);
      doc.setTextColor(156, 163, 175);
      doc.text('Profit Share', 130 + (yearWidth + 25) * 3, yearY + 85, { align: 'center' });
      doc.setDrawColor(51, 51, 51);
      doc.line(70 + (yearWidth + 25) * 3, yearY + 100, 190 + (yearWidth + 25) * 3, yearY + 100);
      doc.setFontSize(28);
      doc.setTextColor(16, 185, 129);
      doc.text('15%', 130 + (yearWidth + 25) * 3, yearY + 130, { align: 'center' });
      doc.setFontSize(10);
      doc.text('Fully Vested', 130 + (yearWidth + 25) * 3, yearY + 150, { align: 'center' });
      doc.setFontSize(9);
      doc.setTextColor(16, 185, 129);
      doc.text('Full Ownership', 130 + (yearWidth + 25) * 3, yearY + 170, { align: 'center' });
      
      // 1-Year Cliff Note
      doc.setFillColor(30, 20, 20);
      doc.roundedRect(50, 340, pageWidth - 100, 45, 8, 8, 'F');
      doc.setDrawColor(239, 68, 68);
      doc.setLineWidth(1);
      doc.roundedRect(50, 340, pageWidth - 100, 45, 8, 8, 'S');
      doc.setFontSize(11);
      doc.setTextColor(239, 68, 68);
      doc.text('1-YEAR CLIFF: Equity vesting begins after Year 1. Exit before cliff = forfeit equity (keep paid profit share).', pageWidth / 2, 368, { align: 'center' });
      
      // =================== SLIDE 3: Compensation Breakdown ===================
      doc.addPage();
      drawBackground();
      
      doc.setFontSize(10);
      doc.setTextColor(16, 185, 129);
      doc.text('TOTAL COMPENSATION', 50, 50);
      
      doc.setFontSize(28);
      doc.setTextColor(255, 255, 255);
      doc.text('3-Year Earnings Potential', 50, 85);
      
      // Profit Share Column
      drawCard(50, 120, 330, 220, '#a855f7');
      doc.setFontSize(18);
      doc.setTextColor(168, 85, 247);
      doc.text('Net Profit Share (Cash)', 215, 150, { align: 'center' });
      
      doc.setFillColor(45, 45, 45);
      doc.roundedRect(70, 170, 290, 30, 6, 6, 'F');
      doc.setFontSize(11);
      doc.setTextColor(156, 163, 175);
      doc.text('Year 1 (15%)', 85, 190);
      doc.setTextColor(168, 85, 247);
      doc.text('~$270K', 340, 190, { align: 'right' });
      
      doc.setFillColor(45, 45, 45);
      doc.roundedRect(70, 210, 290, 30, 6, 6, 'F');
      doc.setFontSize(11);
      doc.setTextColor(156, 163, 175);
      doc.text('Year 2 (10%)', 85, 230);
      doc.setTextColor(168, 85, 247);
      doc.text('~$500K', 340, 230, { align: 'right' });
      
      doc.setFillColor(45, 45, 45);
      doc.roundedRect(70, 250, 290, 30, 6, 6, 'F');
      doc.setFontSize(11);
      doc.setTextColor(156, 163, 175);
      doc.text('Year 3 (5%)', 85, 270);
      doc.setTextColor(168, 85, 247);
      doc.text('~$700K', 340, 270, { align: 'right' });
      
      doc.setFillColor(60, 40, 80);
      doc.roundedRect(70, 295, 290, 35, 8, 8, 'F');
      doc.setFontSize(24);
      doc.setTextColor(168, 85, 247);
      doc.text('~$1.47M', 215, 320, { align: 'center' });
      
      // Equity Column
      drawCard(410, 120, 330, 220, '#10b981');
      doc.setFontSize(18);
      doc.setTextColor(16, 185, 129);
      doc.text('Equity Value (at Exit)', 575, 150, { align: 'center' });
      
      doc.setFontSize(11);
      doc.setTextColor(209, 213, 219);
      doc.text('SaaS valued at 3-5x ARR', 430, 180);
      doc.text('$18M ARR x 4x = $72M valuation', 430, 200);
      doc.text('Your 15% stake value:', 430, 220);
      
      doc.setFillColor(30, 60, 50);
      doc.roundedRect(430, 240, 290, 55, 8, 8, 'F');
      doc.setFontSize(32);
      doc.setTextColor(16, 185, 129);
      doc.text('~$10.8M', 575, 278, { align: 'center' });
      
      // Total Box
      doc.setFillColor(35, 30, 20);
      doc.roundedRect(50, 360, pageWidth - 100, 70, 10, 10, 'F');
      doc.setDrawColor(245, 158, 11);
      doc.setLineWidth(2);
      doc.roundedRect(50, 360, pageWidth - 100, 70, 10, 10, 'S');
      doc.setFontSize(14);
      doc.setTextColor(156, 163, 175);
      doc.text('TOTAL 3-YEAR POTENTIAL', pageWidth / 2, 385, { align: 'center' });
      doc.setFontSize(36);
      doc.setTextColor(245, 158, 11);
      doc.text('~$12.3M', pageWidth / 2, 420, { align: 'center' });
      
      // =================== SLIDE 4: Legal Framework ===================
      doc.addPage();
      drawBackground();
      
      doc.setFontSize(10);
      doc.setTextColor(245, 158, 11);
      doc.text('LEGAL FRAMEWORK', 50, 50);
      
      doc.setFontSize(28);
      doc.setTextColor(255, 255, 255);
      doc.text('Entity & Protections', 50, 85);
      
      // Entity Info Card
      drawCard(50, 110, 330, 200, '#f59e0b');
      doc.setFontSize(16);
      doc.setTextColor(245, 158, 11);
      doc.text('Legal Entity', 70, 140);
      
      doc.setFontSize(12);
      doc.setTextColor(107, 114, 128);
      doc.text('Company Name', 70, 170);
      doc.setTextColor(255, 255, 255);
      doc.text('GONOWBABY MEDIA GROUP LLC', 70, 188);
      
      doc.setTextColor(107, 114, 128);
      doc.text('Primary Brand', 70, 215);
      doc.setTextColor(245, 158, 11);
      doc.text('MyHairChair', 70, 233);
      
      doc.setTextColor(107, 114, 128);
      doc.text('Founder & Sole Member', 70, 260);
      doc.setTextColor(255, 255, 255);
      doc.text('George Broughton III', 70, 278);
      doc.setTextColor(107, 114, 128);
      doc.setFontSize(10);
      doc.text('Retains 100% voting control during vesting', 70, 295);
      
      // Developer Role Card
      drawCard(410, 110, 330, 200, '#a855f7');
      doc.setFontSize(16);
      doc.setTextColor(168, 85, 247);
      doc.text('Lead Developer Role', 430, 140);
      
      doc.setFontSize(11);
      doc.setTextColor(16, 185, 129);
      const checkY = 165;
      doc.text('✓', 430, checkY);
      doc.setTextColor(209, 213, 219);
      doc.text('Technical leadership (CTO-level)', 450, checkY);
      
      doc.setTextColor(16, 185, 129);
      doc.text('✓', 430, checkY + 25);
      doc.setTextColor(209, 213, 219);
      doc.text('Code architecture ownership', 450, checkY + 25);
      
      doc.setTextColor(16, 185, 129);
      doc.text('✓', 430, checkY + 50);
      doc.setTextColor(209, 213, 219);
      doc.text('Mobile app development lead', 450, checkY + 50);
      
      doc.setTextColor(16, 185, 129);
      doc.text('✓', 430, checkY + 75);
      doc.setTextColor(209, 213, 219);
      doc.text('Ongoing maintenance & support', 450, checkY + 75);
      
      doc.setTextColor(16, 185, 129);
      doc.text('✓', 430, checkY + 100);
      doc.setTextColor(209, 213, 219);
      doc.text('Strategic technology decisions', 450, checkY + 100);
      
      // Termination Clause
      doc.setFillColor(30, 20, 20);
      doc.roundedRect(50, 330, pageWidth - 100, 60, 8, 8, 'F');
      doc.setDrawColor(239, 68, 68);
      doc.setLineWidth(1);
      doc.roundedRect(50, 330, pageWidth - 100, 60, 8, 8, 'S');
      doc.setFontSize(12);
      doc.setTextColor(239, 68, 68);
      doc.text('TERMINATION CLAUSE', 70, 355);
      doc.setFontSize(10);
      doc.setTextColor(209, 213, 219);
      doc.text('If developer exits before 1-year cliff: Forfeit all equity and future profit sharing.', 70, 375);
      doc.text('Paid profit share is retained.', 70, 390);
      
      // =================== SLIDE 5: Contact ===================
      doc.addPage();
      drawBackground();
      
      doc.setFontSize(10);
      doc.setTextColor(245, 158, 11);
      doc.text('NEXT STEPS', 50, 50);
      
      doc.setFontSize(32);
      doc.setTextColor(255, 255, 255);
      doc.text("Let's Build Together", 50, 90);
      
      // Contact cards
      const contactY = 140;
      const contactWidth = 210;
      
      drawCard(50, contactY, contactWidth, 100);
      doc.setFontSize(11);
      doc.setTextColor(156, 163, 175);
      doc.text('Email', 155, contactY + 30, { align: 'center' });
      doc.setFontSize(13);
      doc.setTextColor(255, 255, 255);
      doc.text('george@myhairchair.com', 155, contactY + 55, { align: 'center' });
      
      drawCard(50 + contactWidth + 25, contactY, contactWidth, 100);
      doc.setFontSize(11);
      doc.setTextColor(156, 163, 175);
      doc.text('Website', 155 + contactWidth + 25, contactY + 30, { align: 'center' });
      doc.setFontSize(13);
      doc.setTextColor(255, 255, 255);
      doc.text('MyHairChair.com', 155 + contactWidth + 25, contactY + 55, { align: 'center' });
      
      drawCard(50 + (contactWidth + 25) * 2, contactY, contactWidth, 100);
      doc.setFontSize(11);
      doc.setTextColor(156, 163, 175);
      doc.text('Demo', 155 + (contactWidth + 25) * 2, contactY + 30, { align: 'center' });
      doc.setFontSize(13);
      doc.setTextColor(255, 255, 255);
      doc.text('Live App Available', 155 + (contactWidth + 25) * 2, contactY + 55, { align: 'center' });
      
      // Quote
      doc.setFontSize(14);
      doc.setTextColor(107, 114, 128);
      doc.setFont(undefined, 'italic');
      doc.text('"The Chair is the center of the beauty economy."', pageWidth / 2, 290, { align: 'center' });
      doc.setFont(undefined, 'normal');
      
      // Final CTA
      doc.setFillColor(35, 30, 20);
      doc.roundedRect(50, 320, pageWidth - 100, 70, 10, 10, 'F');
      doc.setDrawColor(245, 158, 11);
      doc.setLineWidth(2);
      doc.roundedRect(50, 320, pageWidth - 100, 70, 10, 10, 'S');
      doc.setFontSize(14);
      doc.setTextColor(245, 158, 11);
      doc.text('15% Equity (3-Yr Vest) | 15% Profit Share | ~$12.3M Total Potential', pageWidth / 2, 355, { align: 'center' });
      doc.setFontSize(10);
      doc.setTextColor(156, 163, 175);
      doc.text('GONOWBABY MEDIA GROUP LLC', pageWidth / 2, 375, { align: 'center' });
      
      // Save the PDF
      doc.save('MyHairChair_Lead_Developer_Partnership_Proposal.pdf');
      
      toast.success('PDF downloaded successfully!');
      console.log('PDF generation complete!');
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.error('Failed to generate PDF. Please try again.');
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const projections = [
    { year: "2026", revenue: "$1.8M", subscribers: "4,000", profit: "$1.2M", totalUsers: "15,000" },
    { year: "2027", revenue: "$6.5M", subscribers: "15,000", profit: "$5M", totalUsers: "60,000" },
    { year: "2028", revenue: "$18M", subscribers: "40,000", profit: "$14M", totalUsers: "150,000" }
  ];

  const partnerBenefits = [
    { title: "15% Profit Share (Year 1)", description: "Quarterly distributions from net platform revenue", icon: DollarSign },
    { title: "15% Equity (3-Year Vest)", description: "5% vests each year after 1-year cliff", icon: PieChart },
    { title: "Performance-Driven", description: "Immediate cash flow + long-term ownership", icon: TrendingUp },
    { title: "High Exit Potential", description: "SaaS companies valued at 3-5x revenue", icon: Rocket }
  ];

  const problemsToSolve = [
    { issue: "Fragmented Scheduling", impact: "Double-bookings, no-shows, lost revenue", icon: Calendar },
    { issue: "No Client Management", impact: "Poor retention, no loyalty tracking", icon: Users },
    { issue: "Booth Rental Chaos", impact: "Unclear revenue splits, payment disputes", icon: Building2 },
    { issue: "Compliance Tracking", impact: "Missed license renewals, legal risks", icon: Shield }
  ];

  const futureRevenue = [
    { stream: "CE Course Platform", potential: "$350K/year", description: "Bi-annual hair license renewal prep courses", icon: BookOpen },
    { stream: "Product Marketplace", potential: "$200K/year", description: "Professional salon supplies & equipment", icon: ShoppingBag },
    { stream: "Insurance Partnerships", potential: "$50K/year", description: "Referral commissions from policy sales", icon: Shield },
    { stream: "Payroll Integration", potential: "$100K/year", description: "Partner revenue share", icon: DollarSign }
  ];

  return (
    <div className="min-h-screen bg-black text-white">
      {/* SEO Meta Tags for Social Sharing */}
      <SEOMeta 
        title="MyHairChair - Lead Developer Partnership | 15% Equity + Profit Share"
        description="Hybrid compensation: 15% profit share converting to 15% equity over 3 years. Build the future of salon commerce with GoNowBaby Media Group."
        url="https://myhairchair.com/partnership"
        image="https://static.prod-images.emergentagent.com/jobs/2f9006eb-b332-428d-86e9-eb851b9d0232/images/63d58951e5ab7e31ae4c087a16e2dd8d29cd67a8c02b18e2b7a2b5eca234ad44.png"
        type="article"
      />
      
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-black/90 backdrop-blur-xl border-b border-amber-500/20">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-amber-400 via-yellow-500 to-amber-600 rounded-xl flex items-center justify-center shadow-lg shadow-amber-500/30">
                <Scissors className="w-5 h-5 text-black" />
              </div>
              <span className="font-bold text-xl bg-gradient-to-r from-amber-400 to-yellow-500 bg-clip-text text-transparent">MyHairChair</span>
            </Link>
            <div className="flex items-center gap-4">
              {/* Share Button */}
              <Button 
                variant="ghost" 
                className="text-gray-300 hover:text-amber-400 flex items-center gap-2"
                onClick={() => handleShare('native')}
              >
                <Share2 className="w-4 h-4" />
                <span className="hidden sm:inline">Share</span>
              </Button>
              <Link to="/">
                <Button variant="ghost" className="text-gray-300 hover:text-amber-400">
                  Back to App
                </Button>
              </Link>
              <a href="mailto:info@myhairchair.com">
                <Button className="bg-gradient-to-r from-amber-500 to-yellow-600 hover:from-amber-600 hover:to-yellow-700 text-black font-semibold">
                  Contact Us
                </Button>
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center pt-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-900 to-black"></div>
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-amber-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>

        <div className="relative max-w-6xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 bg-purple-500/10 border border-purple-500/30 px-4 py-2 rounded-full mb-8">
            <Handshake className="w-4 h-4 text-purple-400" />
            <span className="text-purple-400 text-sm font-medium">Strategic Partnership Proposal</span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold mb-6 leading-tight">
            <span className="text-white">App Developer</span>
            <br />
            <span className="bg-gradient-to-r from-amber-400 via-yellow-500 to-amber-400 bg-clip-text text-transparent">
              Partnership Opportunity
            </span>
          </h1>

          <p className="text-xl text-gray-400 mb-4 max-w-3xl mx-auto">
            Join us as Lead Developer and build the future of salon commerce
          </p>

          {/* Key Stats - Updated Hybrid Model */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto my-12">
            <div className="bg-gradient-to-br from-amber-500/20 to-transparent border border-amber-500/30 rounded-2xl p-6">
              <div className="text-3xl font-bold text-amber-400">15%</div>
              <div className="text-sm text-gray-400">Equity (3-Yr Vest)</div>
            </div>
            <div className="bg-gradient-to-br from-green-500/20 to-transparent border border-green-500/30 rounded-2xl p-6">
              <div className="text-3xl font-bold text-green-400">15%</div>
              <div className="text-sm text-gray-400">Profit Share (Yr 1)</div>
            </div>
            <div className="bg-gradient-to-br from-purple-500/20 to-transparent border border-purple-500/30 rounded-2xl p-6">
              <div className="text-3xl font-bold text-purple-400">$18M</div>
              <div className="text-sm text-gray-400">Year 3 ARR Target</div>
            </div>
            <div className="bg-gradient-to-br from-cyan-500/20 to-transparent border border-cyan-500/30 rounded-2xl p-6">
              <div className="text-3xl font-bold text-cyan-400">77%+</div>
              <div className="text-sm text-gray-400">Profit Margins</div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
            <Button 
              onClick={handleDownloadClick}
              disabled={isGeneratingPDF}
              className="bg-gradient-to-r from-amber-500 to-yellow-600 hover:from-amber-600 hover:to-yellow-700 text-black text-lg px-10 py-6 rounded-xl shadow-xl shadow-amber-500/30 font-bold"
            >
              {isGeneratingPDF ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Generating PDF...
                </>
              ) : hasAccess ? (
                <>
                  <Download className="w-5 h-5 mr-2" />
                  Download Full Proposal (PDF)
                </>
              ) : (
                <>
                  <Lock className="w-5 h-5 mr-2" />
                  Get Instant Access
                </>
              )}
            </Button>
            <a href="#projections">
              <Button variant="outline" className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10 text-lg px-8 py-6 rounded-xl">
                <BarChart3 className="w-5 h-5 mr-2" />
                View Projections
              </Button>
            </a>
          </div>

          {hasAccess && (
            <div className="inline-flex items-center gap-2 bg-emerald-500/20 border border-emerald-500/50 px-4 py-2 rounded-full mb-8">
              <CheckCircle className="w-4 h-4 text-emerald-400" />
              <span className="text-emerald-400 text-sm font-medium">
                Welcome, {localStorage.getItem('partner_name') || 'Partner'}! Full access granted.
              </span>
            </div>
          )}

          {/* Share This Proposal */}
          <div className="flex flex-col items-center gap-3 mb-8">
            <p className="text-gray-400 text-sm">Share this opportunity</p>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                className="text-gray-400 hover:text-white hover:bg-white/10 rounded-full p-2"
                onClick={() => handleShare('copy')}
                title="Copy Link"
              >
                <Copy className="w-5 h-5" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="text-gray-400 hover:text-[#1DA1F2] hover:bg-[#1DA1F2]/10 rounded-full p-2"
                onClick={() => handleShare('twitter')}
                title="Share on X/Twitter"
              >
                <Twitter className="w-5 h-5" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="text-gray-400 hover:text-[#0A66C2] hover:bg-[#0A66C2]/10 rounded-full p-2"
                onClick={() => handleShare('linkedin')}
                title="Share on LinkedIn"
              >
                <Linkedin className="w-5 h-5" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="text-gray-400 hover:text-[#1877F2] hover:bg-[#1877F2]/10 rounded-full p-2"
                onClick={() => handleShare('facebook')}
                title="Share on Facebook"
              >
                <Facebook className="w-5 h-5" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="text-gray-400 hover:text-[#25D366] hover:bg-[#25D366]/10 rounded-full p-2"
                onClick={() => handleShare('whatsapp')}
                title="Share on WhatsApp"
              >
                <MessageCircle className="w-5 h-5" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="text-gray-400 hover:text-amber-400 hover:bg-amber-400/10 rounded-full p-2"
                onClick={() => handleShare('email')}
                title="Share via Email"
              >
                <Mail className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </div>

        {/* Access Form Modal */}
        <Dialog open={showAccessForm} onOpenChange={setShowAccessForm}>
          <DialogContent className="bg-gray-900 border-amber-500/30 text-white max-w-md">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold text-center">
                <span className="bg-gradient-to-r from-amber-400 to-yellow-500 bg-clip-text text-transparent">
                  Get Partnership Details
                </span>
              </DialogTitle>
            </DialogHeader>
            
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-amber-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Handshake className="w-8 h-8 text-white" />
              </div>
              <p className="text-gray-400">
                Enter your details to access the full partnership proposal
              </p>
            </div>

            <form onSubmit={handleAccessSubmit} className="space-y-4">
              <div>
                <Label className="text-gray-300">Full Name *</Label>
                <div className="relative mt-2">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <Input
                    type="text"
                    placeholder="John Smith"
                    value={leadForm.name}
                    onChange={(e) => setLeadForm({ ...leadForm, name: e.target.value })}
                    className="pl-10 bg-gray-800 border-gray-700 text-white placeholder:text-gray-500 focus:border-amber-500"
                    required
                  />
                </div>
              </div>

              <div>
                <Label className="text-gray-300">Email Address *</Label>
                <div className="relative mt-2">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <Input
                    type="email"
                    placeholder="john@company.com"
                    value={leadForm.email}
                    onChange={(e) => setLeadForm({ ...leadForm, email: e.target.value })}
                    className="pl-10 bg-gray-800 border-gray-700 text-white placeholder:text-gray-500 focus:border-amber-500"
                    required
                  />
                </div>
              </div>

              <div>
                <Label className="text-gray-300">Company (Optional)</Label>
                <div className="relative mt-2">
                  <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <Input
                    type="text"
                    placeholder="Your Development Agency"
                    value={leadForm.company}
                    onChange={(e) => setLeadForm({ ...leadForm, company: e.target.value })}
                    className="pl-10 bg-gray-800 border-gray-700 text-white placeholder:text-gray-500 focus:border-amber-500"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-amber-500 to-yellow-600 hover:from-amber-600 hover:to-yellow-700 text-black font-semibold py-6 rounded-xl"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Unlock className="w-5 h-5 mr-2" />
                    Get Instant Access
                  </>
                )}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </section>

      {/* Problem We're Solving */}
      <section className="py-24 bg-gradient-to-b from-black to-gray-900" id="problem">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-red-400 font-medium mb-4 block tracking-widest uppercase">The Problem</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              The <span className="text-red-400">$97 Billion</span> Problem
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              The salon and barber shop industry is plagued by outdated systems and manual processes
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {problemsToSolve.map((problem, index) => (
              <Card key={index} className="bg-gradient-to-br from-red-500/10 to-transparent border-red-500/20 hover:border-red-500/40 transition-all">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-red-500/20 rounded-xl flex items-center justify-center mb-4">
                    <problem.icon className="w-6 h-6 text-red-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">{problem.issue}</h3>
                  <p className="text-gray-400 text-sm">{problem.impact}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Market Stats */}
          <div className="mt-16 bg-gradient-to-r from-amber-500 via-yellow-500 to-amber-500 rounded-3xl p-8 md:p-12">
            <div className="grid md:grid-cols-3 gap-8 text-center">
              <div>
                <div className="text-4xl md:text-5xl font-bold text-black">1.1M+</div>
                <div className="text-black/70 font-medium">Addressable Users (Big Four)</div>
              </div>
              <div>
                <div className="text-4xl md:text-5xl font-bold text-black">$97B</div>
                <div className="text-black/70 font-medium">Industry Market Size</div>
              </div>
              <div>
                <div className="text-4xl md:text-5xl font-bold text-black">13.6%</div>
                <div className="text-black/70 font-medium">Target Market Capture</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* OPEN PAYMENT ECOSYSTEM - Developer Opportunity */}
      <section className="py-24 bg-gradient-to-b from-black via-emerald-950/20 to-black relative" id="open-payment">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-emerald-500/10 via-transparent to-transparent"></div>
        <div className="max-w-7xl mx-auto px-6 relative">
          <div className="text-center mb-16">
            <span className="text-emerald-400 font-medium mb-4 block tracking-widest uppercase">Developer Opportunity</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Build on an <span className="text-emerald-400">Open Payment</span> Ecosystem
            </h2>
            <p className="text-gray-400 max-w-3xl mx-auto">
              Unlike closed-loop platforms that force 2.6-3.5% fees, MyHairChair enables developers to build 
              payment-agnostic solutions that keep 100% of revenue with professionals.
            </p>
          </div>

          {/* Competitor Lock-In vs Open Ecosystem */}
          <div className="grid md:grid-cols-2 gap-8 mb-16">
            {/* Closed Systems */}
            <div className="bg-gradient-to-br from-red-900/30 to-red-950/20 border border-red-500/30 rounded-2xl p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-red-500/20 rounded-xl flex items-center justify-center">
                  <Lock className="w-6 h-6 text-red-400" />
                </div>
                <h3 className="text-xl font-bold text-white">Closed-Loop Platforms</h3>
              </div>
              <div className="space-y-4">
                {[
                  { name: "Square", users: "600K+", fee: "2.6% + 10¢", issue: "Mandatory card processing" },
                  { name: "Vagaro", users: "200K+", fee: "2.75% + 25¢", issue: "Enterprise lock-in" },
                  { name: "StyleSeat", users: "200K+", fee: "2.75% + 25¢", issue: "2-3 day payout delays" },
                  { name: "Booksy", users: "100K+", fee: "2.49% + 15¢", issue: "Limited payment options" }
                ].map((platform, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-black/30 rounded-lg">
                    <div>
                      <span className="text-white font-medium">{platform.name}</span>
                      <span className="text-gray-500 text-sm ml-2">({platform.users})</span>
                    </div>
                    <div className="text-right">
                      <div className="text-red-400 font-bold text-sm">{platform.fee}</div>
                      <div className="text-gray-500 text-xs">{platform.issue}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-6 p-4 bg-red-500/10 rounded-lg border border-red-500/20">
                <div className="text-red-400 font-bold text-2xl">$33M+</div>
                <div className="text-gray-400 text-sm">Annual fees extracted from 1.1M professionals</div>
              </div>
            </div>

            {/* Open Ecosystem */}
            <div className="bg-gradient-to-br from-emerald-900/30 to-green-950/20 border border-emerald-500/30 rounded-2xl p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center">
                  <Unlock className="w-6 h-6 text-emerald-400" />
                </div>
                <h3 className="text-xl font-bold text-white">MyHairChair Open Ecosystem</h3>
              </div>
              <div className="space-y-4">
                {[
                  { feature: "Zelle Integration", benefit: "0% fees, instant transfer", icon: "⚡" },
                  { feature: "CashApp Support", benefit: "0% fees, same-day access", icon: "💵" },
                  { feature: "PayPal Option", benefit: "Flexible payment choice", icon: "🔄" },
                  { feature: "Cash Tracking", benefit: "Full revenue visibility", icon: "📊" }
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-black/30 rounded-lg">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{item.icon}</span>
                      <span className="text-white font-medium">{item.feature}</span>
                    </div>
                    <div className="text-emerald-400 text-sm font-medium">{item.benefit}</div>
                  </div>
                ))}
              </div>
              <div className="mt-6 p-4 bg-emerald-500/10 rounded-lg border border-emerald-500/20">
                <div className="text-emerald-400 font-bold text-2xl">100%</div>
                <div className="text-gray-400 text-sm">Revenue stays with professionals</div>
              </div>
            </div>
          </div>

          {/* Developer Value Proposition */}
          <div className="bg-gradient-to-r from-emerald-500/10 via-green-500/10 to-emerald-500/10 rounded-3xl p-8 border border-emerald-500/30 mb-16">
            <h3 className="text-2xl font-bold text-white text-center mb-8">
              Why Developers Choose <span className="text-emerald-400">Open Payment</span> Architecture
            </h3>
            <div className="grid md:grid-cols-4 gap-6">
              {[
                { title: "API Freedom", desc: "RESTful APIs that integrate with any payment provider", icon: Code },
                { title: "No Lock-In", desc: "Users own their data and payment relationships", icon: Unlock },
                { title: "Instant Payouts", desc: "Build apps that pay professionals immediately", icon: Zap },
                { title: "Niche Features", desc: "Medical-grade scalp logs, health profiles - unique to us", icon: Crown }
              ].map((item, idx) => (
                <div key={idx} className="text-center p-4">
                  <div className="w-14 h-14 bg-emerald-500/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <item.icon className="w-7 h-7 text-emerald-400" />
                  </div>
                  <h4 className="text-white font-bold mb-2">{item.title}</h4>
                  <p className="text-gray-400 text-sm">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Market Capture Target */}
          <div className="text-center p-8 bg-black/50 rounded-3xl border border-emerald-500/20">
            <div className="text-5xl font-bold bg-gradient-to-r from-emerald-400 via-green-400 to-amber-400 bg-clip-text text-transparent mb-4">
              10-15% Market Capture
            </div>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto mb-6">
              Our target: Pull 110,000-165,000 professionals from the Big Four within 3 years through 
              word-of-mouth, trade shows, and our HairCare Soldiers referral program.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              {["Bronner Bros Expo", "International Beauty Show", "Barber School Partnerships", "Salon Suite Chains"].map((channel, idx) => (
                <Badge key={idx} className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 px-4 py-2">
                  {channel}
                </Badge>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Move from AI Platform */}
      <section className="py-24 bg-black" id="why-partner">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-amber-400 font-medium mb-4 block tracking-widest uppercase">The Opportunity</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Why We Need a <span className="text-amber-400">Development Partner</span>
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              We have proven the concept with AI tools - now we need professional expertise to scale
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Current Limitations */}
            <Card className="bg-gradient-to-br from-red-500/10 to-transparent border-red-500/20">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-red-500/20 rounded-xl flex items-center justify-center">
                    <AlertTriangle className="w-6 h-6 text-red-400" />
                  </div>
                  <h3 className="text-xl font-bold text-white">AI Platform Limitations</h3>
                </div>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-red-400 rounded-full mt-2"></div>
                    <div>
                      <p className="text-white font-medium">Technical Debt</p>
                      <p className="text-gray-400 text-sm">8,500+ line server file needs refactoring</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-red-400 rounded-full mt-2"></div>
                    <div>
                      <p className="text-white font-medium">Scalability Constraints</p>
                      <p className="text-gray-400 text-sm">Performance issues as user base grows</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-red-400 rounded-full mt-2"></div>
                    <div>
                      <p className="text-white font-medium">No Native Mobile Apps</p>
                      <p className="text-gray-400 text-sm">iOS & Android require specialized skills</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-red-400 rounded-full mt-2"></div>
                    <div>
                      <p className="text-white font-medium">Security & Compliance</p>
                      <p className="text-gray-400 text-sm">PCI compliance needs professional audit</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* What Partner Brings */}
            <Card className="bg-gradient-to-br from-emerald-500/10 to-transparent border-emerald-500/20">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center">
                    <Rocket className="w-6 h-6 text-emerald-400" />
                  </div>
                  <h3 className="text-xl font-bold text-white">What You Bring</h3>
                </div>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-400 mt-0.5" />
                    <div>
                      <p className="text-white font-medium">Code Architecture</p>
                      <p className="text-gray-400 text-sm">Scalable, maintainable codebase</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-400 mt-0.5" />
                    <div>
                      <p className="text-white font-medium">Security Expertise</p>
                      <p className="text-gray-400 text-sm">PCI compliance, penetration testing</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-400 mt-0.5" />
                    <div>
                      <p className="text-white font-medium">Native Mobile Apps</p>
                      <p className="text-gray-400 text-sm">iOS & Android app store presence</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-400 mt-0.5" />
                    <div>
                      <p className="text-white font-medium">Ongoing Support</p>
                      <p className="text-gray-400 text-sm">Bug fixes, updates, feature releases</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Competitive Analysis */}
      <section className="py-24 bg-gradient-to-b from-black to-gray-900" id="competitive">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-purple-400 font-medium mb-4 block tracking-widest uppercase">Market Position</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Competitive <span className="text-purple-400">Analysis</span>
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              MyHairChair offers 23 unique features that no single competitor provides together
            </p>
          </div>

          <Card className="bg-gray-900/50 border-purple-500/20 overflow-hidden">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-purple-500/10 border-b border-purple-500/20">
                      <th className="text-left p-4 text-white font-semibold">Feature</th>
                      <th className="p-4 text-center text-amber-400 font-bold text-lg">MyHairChair</th>
                      <th className="p-4 text-center text-gray-400">Booksy</th>
                      <th className="p-4 text-center text-gray-400">StyleSeat</th>
                      <th className="p-4 text-center text-gray-400">Vagaro</th>
                      <th className="p-4 text-center text-gray-400">Square</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { feature: "Guest Booking (No Account)", mhc: true, booksy: false, styleseat: false, vagaro: false, square: true, highlight: true },
                      { feature: "Recurring Appointments", mhc: true, booksy: true, styleseat: false, vagaro: true, square: false, highlight: true },
                      { feature: "Smart Service Upsells", mhc: true, booksy: false, styleseat: false, vagaro: false, square: false, highlight: true, isNew: true },
                      { feature: "QR Code Client Invites", mhc: true, booksy: false, styleseat: false, vagaro: false, square: false, highlight: true },
                      { feature: "Digital Business Cards", mhc: true, booksy: false, styleseat: false, vagaro: false, square: false, highlight: true },
                      { feature: "Digital Marketing Toolkit", mhc: true, booksy: false, styleseat: false, vagaro: false, square: false, highlight: true },
                      { feature: "Pro Mini-Site Builder", mhc: true, booksy: false, styleseat: false, vagaro: false, square: false, highlight: true },
                      { feature: "Walk-in Queue Management", mhc: true, booksy: false, styleseat: false, vagaro: true, square: false, highlight: true },
                      { feature: "Multi-Tier Loyalty Status", mhc: true, booksy: false, styleseat: false, vagaro: false, square: false, highlight: true },
                      { feature: "Scalp Care Log & pH Tracking", mhc: true, booksy: false, styleseat: false, vagaro: false, square: false, highlight: true, isNew: true },
                      { feature: "Health-Aware Client Profiles", mhc: true, booksy: false, styleseat: false, vagaro: false, square: false, highlight: true, isNew: true },
                      { feature: "Magic Link (Passwordless)", mhc: true, booksy: false, styleseat: false, vagaro: false, square: false, highlight: true },
                      { feature: "Profile Verification System", mhc: true, booksy: false, styleseat: false, vagaro: false, square: false, highlight: true },
                      { feature: "PWA (App-Like Experience)", mhc: true, booksy: false, styleseat: false, vagaro: false, square: false, highlight: true },
                      { feature: "AI Chatbot Assistant (GPT-5.2)", mhc: true, booksy: false, styleseat: false, vagaro: false, square: false, highlight: true },
                      { feature: "Booth/Chair Rental Mgmt", mhc: true, booksy: false, styleseat: false, vagaro: true, square: false, highlight: true },
                      { feature: "HairChair Soldiers Affiliate", mhc: true, booksy: false, styleseat: false, vagaro: false, square: false, highlight: true, isNew: true },
                      { feature: "Staff Payroll Management", mhc: true, booksy: false, styleseat: false, vagaro: true, square: true, highlight: true, isNew: true },
                      { feature: "AI Video Tutorials (Sora 2)", mhc: true, booksy: false, styleseat: false, vagaro: false, square: false, highlight: true, isNew: true },
                      { feature: "Manual Payment Receipts", mhc: true, booksy: false, styleseat: false, vagaro: false, square: false, highlight: true, isNew: true },
                      { feature: "0% Transaction Fees", mhc: true, booksy: false, styleseat: false, vagaro: false, square: false, highlight: true },
                      { feature: "CashApp/Zelle/PayPal Support", mhc: true, booksy: false, styleseat: false, vagaro: false, square: false, highlight: true },
                      { feature: "Revenue Analytics Dashboard", mhc: true, booksy: true, styleseat: true, vagaro: true, square: true }
                    ].map((row, idx) => (
                      <tr key={idx} className={`border-b border-gray-800 ${row.highlight ? 'bg-amber-500/5' : ''}`}>
                        <td className={`p-3 ${row.highlight ? 'text-white font-medium' : 'text-gray-300'}`}>
                          {row.feature}
                          {row.isNew && <span className="ml-2 text-xs bg-green-500 text-white px-1.5 py-0.5 rounded-full">NEW</span>}
                        </td>
                        <td className="p-3 text-center">
                          {row.mhc ? <CheckCircle className="w-5 h-5 text-green-400 mx-auto" /> : <span className="text-red-400">✗</span>}
                        </td>
                        <td className="p-3 text-center">
                          {row.booksy ? <CheckCircle className="w-4 h-4 text-green-400/50 mx-auto" /> : <span className="text-red-400/50">✗</span>}
                        </td>
                        <td className="p-3 text-center">
                          {row.styleseat ? <CheckCircle className="w-4 h-4 text-green-400/50 mx-auto" /> : <span className="text-red-400/50">✗</span>}
                        </td>
                        <td className="p-3 text-center">
                          {row.vagaro ? <CheckCircle className="w-4 h-4 text-green-400/50 mx-auto" /> : <span className="text-red-400/50">✗</span>}
                        </td>
                        <td className="p-3 text-center">
                          {row.square ? <CheckCircle className="w-4 h-4 text-green-400/50 mx-auto" /> : <span className="text-red-400/50">✗</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Unique Features Grid */}
          <div className="grid grid-cols-2 md:grid-cols-6 lg:grid-cols-12 gap-3 mt-8">
            {[
              { icon: Users, label: "Guest Booking" },
              { icon: Calendar, label: "Recurring" },
              { icon: ShoppingCart, label: "Smart Upsells", isNew: true },
              { icon: QrCode, label: "QR Invites" },
              { icon: CreditCard, label: "Digital Cards" },
              { icon: Globe, label: "Pro Mini-Site" },
              { icon: Beaker, label: "Scalp Care", isNew: true },
              { icon: Heart, label: "Health Profiles", isNew: true },
              { icon: Zap, label: "Magic Link" },
              { icon: Smartphone, label: "PWA Ready" },
              { icon: Shield, label: "Soldiers", isNew: true },
              { icon: DollarSign, label: "0% Fees" }
            ].map((item, idx) => (
              <div key={idx} className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-3 text-center relative">
                {item.isNew && <span className="absolute -top-2 -right-2 text-xs bg-green-500 text-white px-1.5 py-0.5 rounded-full">NEW</span>}
                <item.icon className="w-5 h-5 text-amber-400 mx-auto mb-1" />
                <span className="text-white text-xs font-medium">{item.label}</span>
              </div>
            ))}
          </div>

          <div className="text-center mt-8">
            <div className="inline-flex items-center gap-3 bg-gradient-to-r from-purple-500/20 to-amber-500/20 border border-amber-500/50 rounded-2xl px-8 py-4">
              <Award className="w-6 h-6 text-amber-400" />
              <span className="text-lg">
                <span className="text-amber-400 font-bold">23 Unique Features</span>
                <span className="text-gray-300"> that set us apart from all competitors</span>
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Marketing Strategy Section */}
      <section className="py-24 bg-black relative" id="marketing">
        <div className="absolute inset-0 bg-gradient-to-r from-orange-500/5 via-transparent to-red-500/5"></div>
        <div className="max-w-7xl mx-auto px-6 relative">
          <div className="text-center mb-16">
            <span className="text-orange-400 font-medium mb-4 block tracking-widest uppercase">Go-To-Market Strategy</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              How We <span className="text-orange-400">Grow</span>
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Word-of-mouth driven growth powered by industry insiders who earn lifetime revenue
            </p>
          </div>

          {/* HairCare Soldiers Hero */}
          <Card className="bg-gradient-to-br from-orange-500/10 via-red-500/10 to-purple-500/10 border-orange-500/30 mb-12">
            <CardContent className="p-8 md:p-12">
              <div className="flex flex-col lg:flex-row items-center gap-8">
                <div className="flex-1">
                  <div className="inline-flex items-center gap-2 bg-orange-500/20 px-4 py-2 rounded-full mb-4">
                    <Shield className="w-5 h-5 text-orange-400" />
                    <span className="text-orange-400 font-semibold">Our Secret Weapon</span>
                  </div>
                  <h3 className="text-3xl md:text-4xl font-bold text-white mb-4">
                    HairCare <span className="text-orange-400">Soldiers</span>
                  </h3>
                  <p className="text-gray-300 mb-6 text-lg">
                    An elite network of salon stylists, barbers, and owners who champion MyHairChair 
                    in their communities. They are our MVPs — earning <span className="text-green-400 font-bold">lifetime revenue</span> for 
                    every referral they bring to the platform.
                  </p>
                  <div className="flex flex-wrap gap-4 mb-6">
                    <div className="bg-black/30 px-4 py-2 rounded-lg">
                      <span className="text-orange-400 font-bold text-2xl">50</span>
                      <span className="text-gray-400 text-sm ml-2">Soldiers in Key Markets</span>
                    </div>
                    <div className="bg-black/30 px-4 py-2 rounded-lg">
                      <span className="text-green-400 font-bold text-2xl">∞</span>
                      <span className="text-gray-400 text-sm ml-2">Lifetime Commissions</span>
                    </div>
                  </div>
                  <Link to="/soldiers">
                    <Button className="bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white font-semibold">
                      <Users className="w-4 h-4 mr-2" />
                      Learn About HairCare Soldiers
                      <ChevronRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </div>
                <div className="flex-shrink-0">
                  <div className="w-48 h-48 md:w-64 md:h-64 bg-gradient-to-br from-orange-500 to-red-600 rounded-full flex items-center justify-center shadow-2xl shadow-orange-500/30">
                    <div className="text-center">
                      <Shield className="w-16 h-16 md:w-24 md:h-24 text-white mx-auto mb-2" />
                      <span className="text-white font-bold text-lg md:text-xl">SOLDIERS</span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Marketing Channels */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <Card className="bg-gradient-to-br from-orange-500/10 to-transparent border-orange-500/20">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-orange-500/20 rounded-xl flex items-center justify-center mb-4">
                  <Users className="w-6 h-6 text-orange-400" />
                </div>
                <h4 className="text-xl font-bold text-white mb-2">Word of Mouth</h4>
                <p className="text-gray-400 mb-4">
                  Our best-case scenario. Industry professionals trust recommendations from their peers.
                </p>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center gap-2 text-gray-300">
                    <CheckCircle className="w-4 h-4 text-green-400" />
                    Hair shows & trade events
                  </li>
                  <li className="flex items-center gap-2 text-gray-300">
                    <CheckCircle className="w-4 h-4 text-green-400" />
                    Salon-to-salon referrals
                  </li>
                  <li className="flex items-center gap-2 text-gray-300">
                    <CheckCircle className="w-4 h-4 text-green-400" />
                    Barber shop networks
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-pink-500/10 to-transparent border-pink-500/20">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-pink-500/20 rounded-xl flex items-center justify-center mb-4">
                  <Sparkles className="w-6 h-6 text-pink-400" />
                </div>
                <h4 className="text-xl font-bold text-white mb-2">Social Media</h4>
                <p className="text-gray-400 mb-4">
                  Targeted campaigns on platforms where stylists and clients connect.
                </p>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center gap-2 text-gray-300">
                    <CheckCircle className="w-4 h-4 text-green-400" />
                    Instagram & TikTok content
                  </li>
                  <li className="flex items-center gap-2 text-gray-300">
                    <CheckCircle className="w-4 h-4 text-green-400" />
                    Influencer partnerships
                  </li>
                  <li className="flex items-center gap-2 text-gray-300">
                    <CheckCircle className="w-4 h-4 text-green-400" />
                    Before/after showcases
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-purple-500/10 to-transparent border-purple-500/20">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center mb-4">
                  <Building2 className="w-6 h-6 text-purple-400" />
                </div>
                <h4 className="text-xl font-bold text-white mb-2">Salon Stores</h4>
                <p className="text-gray-400 mb-4">
                  Direct partnerships with salon supply stores and distributors.
                </p>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center gap-2 text-gray-300">
                    <CheckCircle className="w-4 h-4 text-green-400" />
                    Point-of-sale presence
                  </li>
                  <li className="flex items-center gap-2 text-gray-300">
                    <CheckCircle className="w-4 h-4 text-green-400" />
                    Product bundling deals
                  </li>
                  <li className="flex items-center gap-2 text-gray-300">
                    <CheckCircle className="w-4 h-4 text-green-400" />
                    Training partnerships
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>

          {/* Soldier Activities */}
          <div className="bg-gradient-to-r from-orange-500/10 via-red-500/5 to-orange-500/10 border border-orange-500/20 rounded-2xl p-8">
            <h4 className="text-xl font-bold text-white mb-6 text-center">What HairCare Soldiers Do</h4>
            <div className="grid md:grid-cols-4 gap-6">
              {[
                { icon: Calendar, title: "Hair Shows", desc: "Represent MyHairChair at major industry events" },
                { icon: Users, title: "Collaborate", desc: "Connect with fellow salon owners and stylists" },
                { icon: Gift, title: "Recruit", desc: "Bring new salons onto the platform" },
                { icon: DollarSign, title: "Earn", desc: "Lifetime revenue from every referral" }
              ].map((item, idx) => (
                <div key={idx} className="text-center">
                  <div className="w-14 h-14 bg-orange-500/20 rounded-xl flex items-center justify-center mx-auto mb-3">
                    <item.icon className="w-7 h-7 text-orange-400" />
                  </div>
                  <h5 className="text-white font-semibold mb-1">{item.title}</h5>
                  <p className="text-gray-400 text-sm">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 3-Year Financial Projections */}
      <section className="py-24 bg-gradient-to-b from-gray-900 to-black" id="projections">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-emerald-400 font-medium mb-4 block tracking-widest uppercase">Financial Projections</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              3-Year <span className="text-emerald-400">Revenue Forecast</span>
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Conservative projections based on capturing just 0.1-0.4% of the addressable market
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {projections.map((proj, index) => (
              <Card key={index} className={`bg-gradient-to-br ${index === 2 ? 'from-emerald-500/20 border-emerald-500/40' : 'from-gray-800/50 border-gray-700'} to-transparent transition-all hover:scale-105`}>
                <CardContent className="p-8 text-center">
                  <div className="text-gray-400 text-sm mb-2">{proj.year}</div>
                  <div className={`text-4xl font-bold ${index === 2 ? 'text-emerald-400' : 'text-white'} mb-2`}>{proj.revenue}</div>
                  <div className="text-sm text-gray-400 mb-6">Annual Revenue</div>
                  <div className="border-t border-gray-700 pt-6 grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-xl font-bold text-amber-400">{proj.subscribers}</div>
                      <div className="text-xs text-gray-500">Paid Subscribers</div>
                    </div>
                    <div>
                      <div className="text-xl font-bold text-green-400">{proj.profit}</div>
                      <div className="text-xs text-gray-500">Net Profit</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Summary Stats */}
          <div className="bg-gradient-to-r from-emerald-500/10 via-green-500/5 to-emerald-500/10 border border-emerald-500/30 rounded-2xl p-8">
            <div className="grid md:grid-cols-4 gap-8 text-center">
              <div>
                <div className="text-3xl font-bold text-white">$26.3M</div>
                <div className="text-emerald-400 text-sm">3-Year Total Revenue</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-white">$20.2M</div>
                <div className="text-emerald-400 text-sm">3-Year Net Profit</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-white">77%+</div>
                <div className="text-emerald-400 text-sm">Profit Margins</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-white">40,000</div>
                <div className="text-emerald-400 text-sm">Year 3 Paid Subscribers</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Hybrid Compensation Model */}
      <section className="py-24 bg-black" id="returns">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-purple-400 font-medium mb-4 block tracking-widest uppercase">Hybrid Equity-Profit Model</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              How You <span className="text-purple-400">Get Paid</span>
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              A performance-driven structure that provides immediate cash flow while building long-term ownership
            </p>
          </div>

          {/* Vesting Timeline Visual */}
          <div className="bg-gradient-to-r from-purple-900/20 via-amber-900/20 to-green-900/20 rounded-3xl p-8 mb-12 border border-purple-500/20">
            <h3 className="text-xl font-bold text-white text-center mb-8">3-Year Profit-to-Equity Conversion</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Year 1 */}
              <div className="bg-black/50 rounded-2xl p-6 border border-purple-500/30 relative">
                <div className="absolute -top-3 left-4 bg-purple-500 text-white text-xs font-bold px-3 py-1 rounded-full">YEAR 1</div>
                <div className="mt-4 text-center">
                  <div className="text-3xl font-bold text-purple-400">15%</div>
                  <div className="text-sm text-gray-400 mb-2">Profit Share</div>
                  <div className="h-px bg-gray-700 my-3"></div>
                  <div className="text-2xl font-bold text-gray-500">0%</div>
                  <div className="text-sm text-gray-500">Equity (Cliff)</div>
                </div>
                <div className="mt-4 text-center">
                  <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">Quarterly Payouts</Badge>
                </div>
              </div>

              {/* Year 2 */}
              <div className="bg-black/50 rounded-2xl p-6 border border-amber-500/30 relative">
                <div className="absolute -top-3 left-4 bg-amber-500 text-black text-xs font-bold px-3 py-1 rounded-full">YEAR 2</div>
                <div className="mt-4 text-center">
                  <div className="text-3xl font-bold text-purple-400">10%</div>
                  <div className="text-sm text-gray-400 mb-2">Profit Share</div>
                  <div className="h-px bg-gray-700 my-3"></div>
                  <div className="text-2xl font-bold text-green-400">5%</div>
                  <div className="text-sm text-green-400">Equity Vested</div>
                </div>
                <div className="mt-4 text-center">
                  <Badge className="bg-green-500/20 text-green-400 border-green-500/30">First Vest</Badge>
                </div>
              </div>

              {/* Year 3 */}
              <div className="bg-black/50 rounded-2xl p-6 border border-amber-500/30 relative">
                <div className="absolute -top-3 left-4 bg-amber-500 text-black text-xs font-bold px-3 py-1 rounded-full">YEAR 3</div>
                <div className="mt-4 text-center">
                  <div className="text-3xl font-bold text-purple-400">5%</div>
                  <div className="text-sm text-gray-400 mb-2">Profit Share</div>
                  <div className="h-px bg-gray-700 my-3"></div>
                  <div className="text-2xl font-bold text-green-400">10%</div>
                  <div className="text-sm text-green-400">Equity Vested</div>
                </div>
                <div className="mt-4 text-center">
                  <Badge className="bg-green-500/20 text-green-400 border-green-500/30">Second Vest</Badge>
                </div>
              </div>

              {/* Year 4+ */}
              <div className="bg-black/50 rounded-2xl p-6 border border-green-500/30 relative">
                <div className="absolute -top-3 left-4 bg-green-500 text-black text-xs font-bold px-3 py-1 rounded-full">YEAR 4+</div>
                <div className="mt-4 text-center">
                  <div className="text-3xl font-bold text-gray-500">0%</div>
                  <div className="text-sm text-gray-500 mb-2">Profit Share</div>
                  <div className="h-px bg-gray-700 my-3"></div>
                  <div className="text-2xl font-bold text-green-400">15%</div>
                  <div className="text-sm text-green-400">Fully Vested</div>
                </div>
                <div className="mt-4 text-center">
                  <Badge className="bg-green-500/20 text-green-400 border-green-500/30">Full Ownership</Badge>
                </div>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Profit Share Card */}
            <Card className="bg-gradient-to-br from-purple-500/10 to-transparent border-purple-500/30">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center">
                    <DollarSign className="w-6 h-6 text-purple-400" />
                  </div>
                  <h3 className="text-xl font-bold text-white">15% Net Profit Share</h3>
                </div>
                <p className="text-gray-400 mb-6">Quarterly distributions based on MyHairChair platform net revenue (Gross revenue minus operating/Stripe/server costs)</p>
                <div className="space-y-4 mb-6">
                  <div className="flex justify-between items-center p-3 bg-gray-800/50 rounded-lg">
                    <span className="text-gray-400">Year 1 (15% share)</span>
                    <span className="text-purple-400 font-bold">~$270K</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-gray-800/50 rounded-lg">
                    <span className="text-gray-400">Year 2 (10% share)</span>
                    <span className="text-purple-400 font-bold">~$500K</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-gray-800/50 rounded-lg">
                    <span className="text-gray-400">Year 3 (5% share)</span>
                    <span className="text-purple-400 font-bold">~$700K</span>
                  </div>
                </div>
                <div className="bg-purple-500/20 rounded-xl p-6 text-center">
                  <div className="text-4xl font-bold text-purple-400">~$1.47M</div>
                  <div className="text-gray-400 text-sm">3-Year Profit Share (Cash)</div>
                </div>
              </CardContent>
            </Card>

            {/* Equity Vesting Card */}
            <Card className="bg-gradient-to-br from-green-500/10 to-transparent border-green-500/30">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-green-500/20 rounded-xl flex items-center justify-center">
                    <PieChart className="w-6 h-6 text-green-400" />
                  </div>
                  <h3 className="text-xl font-bold text-white">15% Equity Vesting</h3>
                </div>
                <p className="text-gray-400 mb-6">Common Stock in GONOWBABY MEDIA GROUP LLC. 5% vests each year after 1-year cliff.</p>
                <div className="space-y-4 mb-6">
                  <div className="flex justify-between items-center p-3 bg-gray-800/50 rounded-lg">
                    <span className="text-gray-400">After Year 1 (cliff)</span>
                    <span className="text-green-400 font-bold">5% Vested</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-gray-800/50 rounded-lg">
                    <span className="text-gray-400">After Year 2</span>
                    <span className="text-green-400 font-bold">10% Vested</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-gray-800/50 rounded-lg">
                    <span className="text-gray-400">After Year 3</span>
                    <span className="text-green-400 font-bold">15% Fully Vested</span>
                  </div>
                </div>
                <div className="bg-green-500/20 rounded-xl p-6 text-center">
                  <div className="text-4xl font-bold text-green-400">~$10.8M</div>
                  <div className="text-gray-400 text-sm">Exit Value (15% of $72M valuation)</div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Total Compensation Summary */}
          <div className="mt-12 bg-gradient-to-r from-amber-500/20 via-yellow-500/20 to-amber-500/20 rounded-3xl p-8 border border-amber-500/30 text-center">
            <h3 className="text-2xl font-bold text-white mb-4">Total 3-Year Compensation Potential</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <div className="text-3xl font-bold text-purple-400">~$1.47M</div>
                <div className="text-gray-400">Cash (Profit Share)</div>
              </div>
              <div className="text-3xl font-bold text-gray-500">+</div>
              <div>
                <div className="text-3xl font-bold text-green-400">~$10.8M</div>
                <div className="text-gray-400">Equity (at Exit)</div>
              </div>
            </div>
            <div className="mt-6 pt-6 border-t border-amber-500/30">
              <div className="text-5xl font-bold text-amber-400">~$12.3M</div>
              <div className="text-gray-300">Total Potential Value</div>
            </div>
          </div>
        </div>
      </section>

      {/* Legal Framework */}
      <section className="py-24 bg-gradient-to-b from-gray-900 to-black" id="structure">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-amber-400 font-medium mb-4 block tracking-widest uppercase">Legal Framework</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Partnership <span className="text-amber-400">Structure</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Entity Info */}
            <Card className="bg-gradient-to-br from-amber-500/10 to-transparent border-amber-500/30">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-amber-500/20 rounded-xl flex items-center justify-center">
                    <Building2 className="w-6 h-6 text-amber-400" />
                  </div>
                  <h3 className="text-xl font-bold text-white">Legal Entity</h3>
                </div>
                <div className="space-y-4">
                  <div className="p-4 bg-gray-800/50 rounded-lg">
                    <div className="text-sm text-gray-500 mb-1">Legal Entity</div>
                    <div className="text-white font-semibold">GONOWBABY MEDIA GROUP LLC</div>
                  </div>
                  <div className="p-4 bg-gray-800/50 rounded-lg">
                    <div className="text-sm text-gray-500 mb-1">Primary Brand</div>
                    <div className="text-amber-400 font-semibold">MyHairChair</div>
                    <div className="text-gray-400 text-sm">A GoNowBaby Media Group Brand</div>
                  </div>
                  <div className="p-4 bg-gray-800/50 rounded-lg">
                    <div className="text-sm text-gray-500 mb-1">Founder & Sole Member</div>
                    <div className="text-white font-semibold">George Broughton III</div>
                    <div className="text-gray-400 text-sm">Retains 100% voting control during vesting</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Developer Role */}
            <Card className="bg-gradient-to-br from-purple-500/10 to-transparent border-purple-500/30">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center">
                    <Code className="w-6 h-6 text-purple-400" />
                  </div>
                  <h3 className="text-xl font-bold text-white">Lead Developer Role</h3>
                </div>
                <div className="text-left space-y-3">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-400" />
                    <span className="text-gray-300">Technical leadership (CTO-level)</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-400" />
                    <span className="text-gray-300">Code architecture ownership</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-400" />
                    <span className="text-gray-300">Mobile app development lead</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-400" />
                    <span className="text-gray-300">Ongoing maintenance & support</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-400" />
                    <span className="text-gray-300">Strategic technology decisions</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Termination Clause */}
          <div className="mt-8 bg-red-900/20 border border-red-500/30 rounded-2xl p-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-red-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <h4 className="text-lg font-bold text-red-400 mb-2">Termination Clause</h4>
                <p className="text-gray-300">
                  If the developer exits before the 1-year cliff, they forfeit all equity and future profit sharing, 
                  though they keep any profit share already paid out.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Future Revenue Expansion */}
      <section className="py-24 bg-black" id="future">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-cyan-400 font-medium mb-4 block tracking-widest uppercase">Growth Potential</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Future <span className="text-cyan-400">Revenue Expansion</span>
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Beyond subscriptions - multiple paths to grow revenue
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {futureRevenue.map((item, index) => (
              <Card key={index} className="bg-gradient-to-br from-cyan-500/10 to-transparent border-cyan-500/20 hover:border-cyan-500/40 transition-all">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-cyan-500/20 rounded-xl flex items-center justify-center mb-4">
                    <item.icon className="w-6 h-6 text-cyan-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-1">{item.stream}</h3>
                  <div className="text-2xl font-bold text-cyan-400 mb-2">{item.potential}</div>
                  <p className="text-gray-400 text-sm">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* CE Course Highlight */}
          <div className="mt-12 bg-gradient-to-r from-blue-500/10 via-purple-500/10 to-blue-500/10 border border-blue-500/30 rounded-2xl p-8">
            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="w-20 h-20 bg-blue-500/20 rounded-2xl flex items-center justify-center flex-shrink-0">
                <Award className="w-10 h-10 text-blue-400" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-white mb-2">Bi-Annual License Renewal Market</h3>
                <p className="text-gray-400 mb-4">
                  Most states require 4-16 hours of continuing education every 1-2 years for cosmetologists and barbers. 
                  With 1.1M+ professionals on competitor platforms, this represents a <span className="text-blue-400 font-bold">$38M - $116M annual market</span>.
                </p>
                <div className="flex flex-wrap gap-4">
                  <div className="bg-gray-800/50 px-4 py-2 rounded-lg">
                    <span className="text-gray-400">Texas:</span> <span className="text-white">4 hrs every 2 years</span>
                  </div>
                  <div className="bg-gray-800/50 px-4 py-2 rounded-lg">
                    <span className="text-gray-400">Florida:</span> <span className="text-white">16 hrs every 2 years</span>
                  </div>
                  <div className="bg-gray-800/50 px-4 py-2 rounded-lg">
                    <span className="text-gray-400">Course Price:</span> <span className="text-white">$25-$75 each</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Low Startup Costs */}
      <section className="py-24 bg-gradient-to-b from-gray-900 to-black">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-emerald-400 font-medium mb-4 block tracking-widest uppercase">Investment Required</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Low <span className="text-emerald-400">Startup Costs</span>
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              The MVP is already built - you are joining at the perfect inflection point
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <Card className="bg-gradient-to-br from-emerald-500/10 to-transparent border-emerald-500/30">
              <CardContent className="p-8">
                <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                  <CheckCircle className="w-6 h-6 text-emerald-400" />
                  Already Built (Value: $150K+)
                </h3>
                <div className="space-y-4">
                  <div className="flex justify-between p-3 bg-gray-800/50 rounded-lg">
                    <span className="text-gray-300">Complete Booking System</span>
                    <span className="text-emerald-400">✓</span>
                  </div>
                  <div className="flex justify-between p-3 bg-gray-800/50 rounded-lg">
                    <span className="text-gray-300">Payment Integration (Stripe)</span>
                    <span className="text-emerald-400">✓</span>
                  </div>
                  <div className="flex justify-between p-3 bg-gray-800/50 rounded-lg">
                    <span className="text-gray-300">Booth Rental Management</span>
                    <span className="text-emerald-400">✓</span>
                  </div>
                  <div className="flex justify-between p-3 bg-gray-800/50 rounded-lg">
                    <span className="text-gray-300">Loyalty & Rewards Engine</span>
                    <span className="text-emerald-400">✓</span>
                  </div>
                  <div className="flex justify-between p-3 bg-gray-800/50 rounded-lg">
                    <span className="text-gray-300">User Authentication</span>
                    <span className="text-emerald-400">✓</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-amber-500/10 to-transparent border-amber-500/30">
              <CardContent className="p-8">
                <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                  <Target className="w-6 h-6 text-amber-400" />
                  Investment Needed (~$130K)
                </h3>
                <div className="space-y-4">
                  <div className="flex justify-between p-3 bg-gray-800/50 rounded-lg">
                    <span className="text-gray-300">Code Refactoring</span>
                    <span className="text-amber-400">$25K</span>
                  </div>
                  <div className="flex justify-between p-3 bg-gray-800/50 rounded-lg">
                    <span className="text-gray-300">Native Mobile Apps</span>
                    <span className="text-amber-400">$50K</span>
                  </div>
                  <div className="flex justify-between p-3 bg-gray-800/50 rounded-lg">
                    <span className="text-gray-300">Security Audit</span>
                    <span className="text-amber-400">$15K</span>
                  </div>
                  <div className="flex justify-between p-3 bg-gray-800/50 rounded-lg">
                    <span className="text-gray-300">Infrastructure Upgrade</span>
                    <span className="text-amber-400">$10K</span>
                  </div>
                  <div className="flex justify-between p-3 bg-gray-800/50 rounded-lg">
                    <span className="text-gray-300">Marketing Launch</span>
                    <span className="text-amber-400">$20K</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Partner Benefits */}
      <section className="py-24 bg-black">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-amber-400 font-medium mb-4 block tracking-widest uppercase">Why Join</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Partner <span className="text-amber-400">Benefits</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {partnerBenefits.map((benefit, index) => (
              <Card key={index} className="bg-gradient-to-br from-amber-500/10 to-transparent border-amber-500/20 hover:border-amber-500/40 transition-all group">
                <CardContent className="p-6 text-center">
                  <div className="w-14 h-14 bg-amber-500/20 rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:bg-amber-500 transition-colors">
                    <benefit.icon className="w-7 h-7 text-amber-400 group-hover:text-black transition-colors" />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">{benefit.title}</h3>
                  <p className="text-gray-400 text-sm">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-b from-gray-900 to-black">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="bg-gradient-to-br from-amber-500/20 to-purple-500/20 border border-amber-500/30 rounded-3xl p-12">
            <div className="w-20 h-20 bg-gradient-to-br from-amber-400 to-yellow-600 rounded-full flex items-center justify-center mx-auto mb-6 text-black font-bold text-3xl">
              GB
            </div>
            <h2 className="text-3xl font-bold text-white mb-4">Let us Build Together</h2>
            <p className="text-gray-400 mb-8 max-w-2xl mx-auto">
              Join us as a technical co-founder and help build the future of salon commerce. 
              The opportunity is massive, the foundation is laid, and the timing is perfect.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <a href="mailto:info@myhairchair.com">
                <Button className="bg-gradient-to-r from-amber-500 to-yellow-600 hover:from-amber-600 hover:to-yellow-700 text-black text-lg px-10 py-6 rounded-xl shadow-xl shadow-amber-500/30 font-bold">
                  <Mail className="w-5 h-5 mr-2" />
                  Contact Us Today
                </Button>
              </a>
              <Button 
                onClick={handleDownloadClick}
                variant="outline" 
                className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10 text-lg px-8 py-6 rounded-xl"
              >
                <Download className="w-5 h-5 mr-2" />
                Download Proposal
              </Button>
            </div>

            <div className="text-sm text-gray-500">
              <p>George Broughton III | Founder & CEO</p>
              <p>MyHairChair | A Gonowbaby Media Group LLC Company</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-8 h-8 bg-gradient-to-br from-amber-400 to-yellow-600 rounded-lg flex items-center justify-center">
              <Scissors className="w-4 h-4 text-black" />
            </div>
            <span className="font-bold text-amber-400">MyHairChair</span>
          </div>
          <p className="text-gray-500 text-sm">© 2026 MyHairChair | A Gonowbaby Media Group LLC Company</p>
        </div>
      </footer>
    </div>
  );
};

export default PartnershipPage;
